/**
 * Discover Page
 *
 * Zeigt Mangas, die noch nicht Teil der eigenen Sammlung sind - mit
 * Sortierung nach neuesten/beliebten/kommenden/empfohlenen Titeln.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Discover.module.css';
import { discoverApi, libraryApi, wishlistApi, getMediaUrl } from '../api';
import MangaDetailModal from '../components/common/MangaDetailModal';

const SORT_LABEL_KEYS = {
  NEWEST: 'discover.sortNewest',
  POPULAR: 'discover.sortPopular',
  UPCOMING: 'discover.sortUpcoming',
  RECOMMENDED: 'discover.sortRecommended',
};

function Discover() {
  const { t } = useTranslation();
  const [sort, setSort] = useState('NEWEST');
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [actionState, setActionState] = useState({}); // manga_id -> { loading, message }

  const [selectedManga, setSelectedManga] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchFeed = useCallback(async (activeSort) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await discoverApi.getDiscoverFeed({ cursor: 0, limit: 50, sort: activeSort });
      if (result.success) {
        setItems(result.data?.items || []);
        setNextCursor(result.data?.next_cursor ?? null);
      } else {
        setError(result.error?.message || t('discover.loadError'));
        setItems([]);
      }
    } catch (e) {
      setError(e?.message || t('discover.loadError'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFeed(sort);
  }, [fetchFeed, sort]);

  const handleLoadMore = async () => {
    if (nextCursor == null) return;
    setIsLoadingMore(true);
    try {
      const result = await discoverApi.getDiscoverFeed({ cursor: nextCursor, limit: 50, sort });
      if (result.success) {
        setItems((prev) => [...prev, ...(result.data?.items || [])]);
        setNextCursor(result.data?.next_cursor ?? null);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const setItemAction = (mangaId, patch) => {
    setActionState((prev) => ({ ...prev, [mangaId]: { ...prev[mangaId], ...patch } }));
  };

  const handleAddToLibrary = async (manga) => {
    setItemAction(manga.manga_id, { loading: true, message: null });
    try {
      const result = await libraryApi.addSeries(manga.manga_id);
      if (result.success) {
        setItemAction(manga.manga_id, { loading: false, message: { type: 'success', text: t('discover.addedToCollection') } });
      } else {
        const code = result.error?.code;
        const text = (code === 'ALREADY_OWNED' || code === 'CONFLICT')
          ? t('discover.alreadyInCollection')
          : (result.error?.message || t('discover.addError'));
        setItemAction(manga.manga_id, { loading: false, message: { type: code === 'ALREADY_OWNED' || code === 'CONFLICT' ? 'info' : 'error', text } });
      }
    } catch (e) {
      setItemAction(manga.manga_id, { loading: false, message: { type: 'error', text: e.message } });
    }
    setTimeout(() => setItemAction(manga.manga_id, { message: null }), 4000);
  };

  const handleAddToWishlist = async (manga) => {
    setItemAction(manga.manga_id, { loading: true, message: null });
    try {
      const result = await wishlistApi.addManga(manga.manga_id);
      if (result.success) {
        setItemAction(manga.manga_id, { loading: false, message: { type: 'success', text: t('discover.addedToWishlist') } });
      } else {
        const code = result.error?.code;
        const text = code === 'ALREADY_EXISTS' ? t('discover.alreadyOnWishlist') : (result.error?.message || t('discover.addError'));
        setItemAction(manga.manga_id, { loading: false, message: { type: code === 'ALREADY_EXISTS' ? 'info' : 'error', text } });
      }
    } catch (e) {
      setItemAction(manga.manga_id, { loading: false, message: { type: 'error', text: e.message } });
    }
    setTimeout(() => setItemAction(manga.manga_id, { message: null }), 4000);
  };

  const openDetail = (manga) => {
    setSelectedManga(manga);
    setIsModalOpen(true);
  };

  const closeDetail = () => {
    setIsModalOpen(false);
    setSelectedManga(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>{t('discover.title')}</h1>
          <p>{t('discover.subtitle')}</p>
        </div>

        <div className={styles.sortGroup}>
          <label htmlFor="discover-sort">{t('discover.sortLabel')}</label>
          <select
            id="discover-sort"
            className={styles.sortSelect}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {discoverApi.SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(SORT_LABEL_KEYS[opt.value] || 'discover.sortNewest')}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>{t('discover.loading')}</p>
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <h2>{t('discover.errorTitle')}</h2>
          <p>{error}</p>
          <button onClick={() => fetchFeed(sort)} className={styles.retryButton} type="button">
            {t('discover.retry')}
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>
          <h2>{t('discover.emptyTitle')}</h2>
          <p>{t('discover.emptyText')}</p>
        </div>
      ) : (
        <>
          <div className={styles.cardGrid}>
            {items.map((manga) => {
              const action = actionState[manga.manga_id] || {};
              return (
                <div key={manga.manga_id} className={styles.card}>
                  <div className={styles.cardCoverWrap} onClick={() => openDetail(manga)}>
                    {manga.cover_media_key ? (
                      <img
                        src={getMediaUrl(manga.cover_media_key)}
                        alt={manga.title}
                        className={styles.cardCover}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles.cardCoverPlaceholder} />
                    )}
                    <div className={styles.badgeRow}>
                      {manga.is_new && <span className={styles.badgeNew}>{t('discover.badgeNew')}</span>}
                      {manga.matches_interests && <span className={styles.badgeMatch}>{t('discover.badgeMatch')}</span>}
                    </div>
                  </div>
                  <div className={styles.cardContent}>
                    <h3 className={styles.cardTitle} onClick={() => openDetail(manga)}>{manga.title}</h3>
                    {manga.publisher && <span className={styles.cardPublisher}>{manga.publisher}</span>}
                    {manga.band_count > 0 && <span className={styles.cardBandCount}>{t('discover.bandCount', { count: manga.band_count })}</span>}
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      type="button"
                      className={styles.cardActionPrimary}
                      onClick={() => handleAddToLibrary(manga)}
                      disabled={action.loading}
                    >
                      + {t('discover.collection')}
                    </button>
                    <button
                      type="button"
                      className={styles.cardActionSecondary}
                      onClick={() => handleAddToWishlist(manga)}
                      disabled={action.loading}
                      title={t('discover.addToWishlistTitle')}
                    >
                      ♥
                    </button>
                  </div>
                  {action.message && (
                    <div className={`${styles.cardMessage} ${styles[action.message.type]}`}>
                      {action.message.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {nextCursor != null && (
            <div className={styles.loadMoreRow}>
              <button
                type="button"
                className={styles.loadMoreButton}
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('discover.loadingShort') : t('discover.loadMore')}
              </button>
            </div>
          )}
        </>
      )}

      {selectedManga && (
        <MangaDetailModal
          isOpen={isModalOpen}
          onClose={closeDetail}
          mangaId={selectedManga.manga_id}
          coverUrl={getMediaUrl(selectedManga.cover_media_key)}
          mangaTitle={selectedManga.title}
        />
      )}
    </div>
  );
}

export default Discover;
